import { mapActions, mapGetters } from 'vuex';
import { BET_FREEZE_AVAILABILITY_MAX_BETS, BET_FREEZE_AVAILABILITY_DELAY_MS } from '@/store/modules/betFreeze';

/**
 * betFreeze in a bet list: the "a selection can be frozen" teaser and the
 * availability sweep behind it. Shared by the classic MyBets and the
 * American IllinoisMyBets.
 *
 * The host component provides `isPlayerEligibleForCashout` and calls
 * `_checkBetFreezeForList()` once its bet list (`user/getMyBets`) is loaded.
 */
export default {
  computed: {
    ...mapGetters({
      getBetFreeze: 'settings/getBetFreeze',
      getBetFreezeAvailability: 'betFreeze/getBetFreezeAvailability',
      getMyBets: 'user/getMyBets',
    }),
  },

  created() {
    // Teaser sweep (non-reactive): run counter + destroyed flag.
    this._betFreezeSweepId = 0;
    this._betFreezeDestroyed = false;
  },

  beforeDestroy() {
    this._betFreezeDestroyed = true;
  },

  methods: {
    ...mapActions({
      checkBetFreezeAvailability: 'betFreeze/checkBetFreezeAvailability',
    }),

    // Pending multiples of a player on a skin with betFreeze on: the only bets
    // worth asking the server about.
    _isBetFreezeListCandidate(bet) {
      return this.getBetFreeze === true
        && this.isPlayerEligibleForCashout
        && Boolean(bet && bet.code)
        && bet.result === 'pending'
        && bet.type === 'multiple';
    },

    betFreezeTeaser(bet) {
      if (!this._isBetFreezeListCandidate(bet)) return false;
      const availability = this.getBetFreezeAvailability(bet.code);
      return Boolean(availability && availability.freezable);
    },

    /**
     * Teaser availability sweep, kept gentle on the API:
     *  - at most BET_FREEZE_AVAILABILITY_MAX_BETS bets (most recent first);
     *  - sequential, one request in flight, BET_FREEZE_AVAILABILITY_DELAY_MS
     *    between two requests actually sent (bets still fresh in the store TTL
     *    are skipped without waiting);
     *  - a new sweep (list reloaded) supersedes the running one, so two sweeps
     *    never overlap; stops as soon as the component is destroyed;
     *  - never throws (the hosts fire it without awaiting): a failure is
     *    logged and only costs the teaser.
     */
    async _checkBetFreezeForList() {
      const runId = ++this._betFreezeSweepId;
      const isStale = () => this._betFreezeDestroyed || runId !== this._betFreezeSweepId;
      try {
        const candidates = (this.getMyBets || [])
          .filter(b => this._isBetFreezeListCandidate(b))
          .slice(0, BET_FREEZE_AVAILABILITY_MAX_BETS);
        let sentBefore = false;
        for (const bet of candidates) {
          if (isStale()) return;
          if (sentBefore) {
            await new Promise(resolve => setTimeout(resolve, BET_FREEZE_AVAILABILITY_DELAY_MS));
            if (isStale()) return;
          }
          sentBefore = await this.checkBetFreezeAvailability({ code: bet.code });
        }
      } catch (e) {
        console.error('[betFreeze] availability sweep failed', e);
      }
    },
  },
};
